import PropTypes from 'prop-types';
import trackDwell from 'decorators/TrackDwell/TrackDwell';
import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { bindActionCreators } from 'redux';
import { userActionCampaign } from 'redux/saga/entities/bound';
import { useReducedNavigation } from 'redux/modules/page';
import { deepExtract } from 'helpers/deep';
import { Image, RES } from 'models/Image';
import { isEmpty } from 'helpers/verify';
import { loadSaga } from './inviteAFriendHero.saga';
import { ClassAvailable } from './ClassAvailable';
import { IAFHeroV2 } from './v2/IAFHeroV2';
import { ClassUnavailable } from './ClassUnavailable';

export class InviteAFriendHeroUnwrapped extends Component {
  componentWillUnmount() {
    this.props.useReducedNavigation(false);
  }

  renderHeroContent() {
    const {
      ctaText,
      legal,
      modules,
      referral: { data },
      title,
      subtitle,
      unavailableSubtitle,
      unavailableTitle,
    } = this.props;

    const availability = deepExtract('schedule.availability.status', data, '');

    // When we fail to load an IAF referral (whether due to downtime or a 404),
    // we display this fallback.
    //
    // Once we fully move to the P2P (V2) version of the page, we should
    // probably stop using <ClassUnavailable /> to display this and instead use
    // a new component with simplified logic to show the pricing page. We should
    // also add translations for the copy on this page.
    if (!(availability === 'available' || availability === 'unavailable')) {
      return (
        <ClassUnavailable
          ctaText="See Plans"
          purchaseUrl="/pricing"
          subtitle="Unfortunately, this promotion is no longer valid. Please double check with your friend for an up-to-date invite code."
          title="Oh no! This promotion is no longer valid."
        />
      );
    }

    // The new P2P variants of the IAF screen have submodules containing the product
    // card (and any additional "right column" components), so we use the
    // presence of modules to decide to render the P2P flow.
    if (!isEmpty(modules)) {
      return (
        <IAFHeroV2
          title={availability === 'available' ? title : unavailableTitle}
          subtitle={availability === 'available' ? subtitle : unavailableSubtitle}
          referrer={data.referrer}
          schedule={data.schedule}
          modules={modules}
        />
      );
    }

    // These legacy components should be removed once we are ready to ship P2P
    // IAF to all markets and browsers.

    if (availability === 'available') {
      return (
        <ClassAvailable
          ctaText={ctaText}
          displayName={deepExtract('referrer.display_name', data)}
          legal={legal}
          purchaseUrl={data.purchase_url}
          schedule={data.schedule}
          subtitle={subtitle}
          title={title}
        />
      );
    }

    return (
      <ClassUnavailable
        ctaText={ctaText}
        displayName={deepExtract('referrer.display_name', data, '')}
        legal={legal}
        purchaseUrl={data.purchase_url}
        subtitle={unavailableSubtitle}
        title={unavailableTitle}
        venueName={deepExtract('schedule.venue.name', data, '')}
      />
    );
  }

  render() {
    const {
      backgroundColor,
      baseUrl,
      referral: { data, loaded },
    } = this.props;

    if (!loaded) {
      return false;
    }

    const imageUrl = Image.getImage(data?.schedule?.venue?.images, RES.high);
    return (
      <div className={`invite-a-friend-hero bg--${backgroundColor}`}>
        <Helmet
          title="ClassPass | You are Invited"
          meta={[
            { property: 'og:url', content: `${baseUrl}invite` },
            { property: 'og:image', content: imageUrl },
            { name: 'robots', content: 'noindex' },
          ]}
        />
        <div className="wrap wrap--narrow">{this.renderHeroContent()}</div>
      </div>
    );
  }
}

InviteAFriendHeroUnwrapped.propMapper = (state, moduleDef) => ({
  backgroundColor: moduleDef.backgroundColor,
  baseUrl: state.request.baseUrl,
  ctaText: moduleDef.ctaText,
  legal: moduleDef.legal,
  modules: moduleDef.modules,
  referral: userActionCampaign.select(state, userActionCampaign.lastRequestedKey(state)),
  subtitle: moduleDef.subtitle,
  title: moduleDef.title,
  unavailableSubtitle: moduleDef.unavailableSubtitle,
  unavailableTitle: moduleDef.unavailableTitle,
});

InviteAFriendHeroUnwrapped.handlerMapper = dispatch => ({
  dispatch,
  ...bindActionCreators(
    {
      useReducedNavigation,
    },
    dispatch
  ),
});

InviteAFriendHeroUnwrapped.propTypes = {
  backgroundColor: PropTypes.oneOf(['light', 'white']),
  baseUrl: PropTypes.string.isRequired,
  ctaText: PropTypes.string.isRequired,
  legal: PropTypes.string,
  modules: PropTypes.array,
  referral: PropTypes.object,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  unavailableSubtitle: PropTypes.string,
  unavailableTitle: PropTypes.string.isRequired,
  useReducedNavigation: PropTypes.func.isRequired,
};

InviteAFriendHeroUnwrapped.loadSaga = loadSaga;

export const InviteAFriendHero = trackDwell(InviteAFriendHeroUnwrapped);
